import { useEffect, useState } from "react";
import { Check, ShieldCheck, X, CalendarClock, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LoadingState } from "@/components/common";
import { QuoteDocument } from "@/modules/quotes/QuoteDocument";
import { fmtTime, fmtDayLong } from "@/modules/scheduling/shared";
import { api, errMsg, fileUrl } from "@/lib/api";
import { contrastText } from "@/lib/format";
import { t } from "@/i18n";

function Shell({ garage, children }) {
  const color = garage?.primary_color || "#0A2540";
  return (
    <div className="min-h-screen bg-slate-50" style={{ "--brand": color, "--brand-contrast": contrastText(color) }} data-testid="secure-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4">
          {garage?.logo_url ? <img src={fileUrl(garage.logo_url)} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <span className="h-10 w-10 rounded-lg" style={{ background: color }} />}
          <p className="font-display text-lg font-bold" data-testid="secure-garage-name">{garage?.name || ""}</p>
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500"><ShieldCheck className="h-4 w-4" />{t("b12.pub.secure")}</span>
        </div>
      </header>
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8">{children}</main>
    </div>
  );
}

const Note = ({ children, testId = "secure-state" }) => <div className="gf-card p-8 text-center text-slate-700" data-testid={testId}>{children}</div>;

function Appointment({ a, tz }) {
  const when = (x) => `${fmtDayLong(x.start, tz)} · ${a.open_ended ? t("b11.dropoffAt", { time: fmtTime(x.start, tz) }) : `${fmtTime(x.start, tz)}–${fmtTime(x.end, tz)}`}`;
  return (
    <div className="gf-card space-y-4 p-6" data-testid="secure-appointment">
      <p className="flex items-center gap-2 font-display text-xl font-bold"><CalendarClock className="h-5 w-5" />{a.service_name}</p>
      <p className="text-sm text-slate-600">{a.vehicle}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-slate-100 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">{t("b12.pub.current")}</p><p className="mt-1 font-semibold line-through decoration-slate-400">{when(a.current)}</p></div>
        <div className="rounded-lg border-2 border-[var(--brand)] p-4"><p className="text-xs uppercase tracking-wider text-slate-500">{t("b12.pub.proposed")}</p><p className="mt-1 font-semibold" data-testid="secure-proposed-time">{when(a.proposal)}</p></div>
      </div>
    </div>
  );
}

function Report({ r, token, tz }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/public/secure/report.pdf", { token }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `condition-report-${r.job_number}.pdf`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const List = ({ rows, tone, label }) => rows.length > 0 && (
    <div><p className={`mb-1 text-xs font-bold uppercase tracking-wider ${tone}`}>{label}</p>
      <ul className="space-y-1 text-sm">{rows.map((x, i) => <li key={i}><strong>{x.label}</strong>{x.action && ` — ${x.action}`}</li>)}</ul></div>
  );
  return (
    <div className="gf-card space-y-5 p-6" data-testid="secure-report">
      <div>
        <p className="font-display text-xl font-bold">{r.name}</p>
        <p className="text-sm text-slate-600">{r.vehicle} · {r.job_number}</p>
        <p className="text-xs text-slate-500">{t("b15.pub.validated", { date: fmtDayLong(r.validated_at, tz) })}</p>
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-bold" data-testid="secure-report-counts">
        {[["urgent", "bg-red-600"], ["monitor", "bg-amber-500"], ["good", "bg-emerald-600"]].map(([k, c]) => r.counts[k] != null && <span key={k} className={`rounded px-2.5 py-1 text-white ${c}`}>{t(`b15.pub.${k}`)}: {r.counts[k]}</span>)}
      </div>
      <List rows={r.urgent} tone="text-red-700" label={t("b15.pub.urgent")} />
      <List rows={r.moderate} tone="text-amber-700" label={t("b15.pub.monitor")} />
      {r.recommendations && <p className="text-sm text-slate-700">{r.recommendations}</p>}
      <Button disabled={busy} onClick={download} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid="secure-report-download"><FileDown className="mr-2 h-4 w-4" />{t("b15.pub.download")}</Button>
    </div>
  );
}

export default function SecureAction() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [data, setData] = useState(null);
  const [error, setError] = useState(token ? "" : t("b12.pub.invalid"));
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (token) api.post("/public/secure/view", { token }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); }, [token]);
  if (error) return <Shell><Note testId="secure-error">{error}</Note></Shell>;
  if (!data) return <LoadingState full />;
  const g = data.garage;
  const decide = async (decision) => {
    setBusy(true);
    try { await api.post("/public/secure/decide", { token, decision, comment }); setDone(decision); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  if (done) return <Shell garage={g}><Note testId="secure-done"><Check className="mx-auto mb-3 h-10 w-10 text-emerald-600" /><p className="font-display text-xl font-bold">{t(`b12.pub.done_${data.kind}_${done}`)}</p><p className="mt-2 text-sm text-slate-500">{t("b12.pub.doneHint", { garage: g.name })}</p></Note></Shell>;
  if (data.state !== "active") return <Shell garage={g}><Note>{data.state === "used" ? t("b12.pub.used") : t(`b12.pub.${data.state}`)}</Note></Shell>;
  if (data.kind === "report") return <Shell garage={g}><Report r={data.report} token={token} tz={g.timezone} /></Shell>;
  const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };
  return (
    <Shell garage={g}>
      {data.kind === "quote" ? <QuoteDocument quote={data.quote} garage={g} /> : <Appointment a={data.appointment} tz={g.timezone} />}
      <div className="gf-card p-6" data-testid="secure-decision">
        <p className="font-display text-lg font-bold">{t("quotes.yourDecision")}</p>
        <p className="mt-1 text-sm text-slate-600">{t("b12.pub.onceHint")}</p>
        {data.kind === "quote" && <Textarea className="mt-4" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("quotes.commentPlaceholder")} data-testid="secure-comment" />}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button disabled={busy} style={brand} onClick={() => decide("accept")} data-testid="secure-accept"><Check className="mr-2 h-4 w-4" />{data.kind === "quote" ? t("quotes.accept") : t("b12.pub.confirm")}</Button>
          <Button disabled={busy} variant="outline" onClick={() => decide("decline")} data-testid="secure-decline"><X className="mr-2 h-4 w-4" />{t("quotes.decline")}</Button>
        </div>
      </div>
    </Shell>
  );
}
